import type { SVGProps } from 'react';
import { juntarClasses } from './classes';

/**
 * Ícones inline.
 *
 * Desenhados em `currentColor` e sem preenchimento próprio: herdam a cor de
 * texto do contexto e, por isso, funcionam nos dois temas sem token novo. São
 * poucos e pequenos de propósito — o legado carregava a biblioteca inteira de
 * ícones do template Fuse para usar uma dúzia.
 */
type PropsIcone = Omit<SVGProps<SVGSVGElement>, 'children'>;

function Base({ className, children, ...resto }: SVGProps<SVGSVGElement>): JSX.Element {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.75}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
      className={juntarClasses('h-4 w-4 shrink-0', className)}
      {...resto}
    >
      {children}
    </svg>
  );
}

export function IconeCheck(props: PropsIcone): JSX.Element {
  return (
    <Base {...props}>
      <path d="m4.5 12.5 5 5 10-11" />
    </Base>
  );
}

export function IconeCadeado(props: PropsIcone): JSX.Element {
  return (
    <Base {...props}>
      <rect x="4.5" y="10.5" width="15" height="10" rx="2" />
      <path d="M8 10.5V7a4 4 0 0 1 8 0v3.5" />
    </Base>
  );
}

export function IconeAlerta(props: PropsIcone): JSX.Element {
  return (
    <Base {...props}>
      <path d="M12 3.8 2.8 19.4h18.4L12 3.8Z" />
      <path d="M12 10v4" />
      <path d="M12 17.2h.01" />
    </Base>
  );
}

export function IconeInformacao(props: PropsIcone): JSX.Element {
  return (
    <Base {...props}>
      <circle cx="12" cy="12" r="9" />
      <path d="M12 11v5.5" />
      <path d="M12 7.8h.01" />
    </Base>
  );
}

/** Caixa vazia — estado sem resultados. */
export function IconeCaixaVazia(props: PropsIcone): JSX.Element {
  return (
    <Base {...props}>
      <path d="M3.5 9.5 12 4l8.5 5.5v5L12 20l-8.5-5.5v-5Z" />
      <path d="M3.5 9.5 12 15l8.5-5.5" />
      <path d="M12 15v5" />
    </Base>
  );
}

/** Documento — estado sem arquivos SPED. */
export function IconeDocumento(props: PropsIcone): JSX.Element {
  return (
    <Base {...props}>
      <path d="M14 3.5H7.5a2 2 0 0 0-2 2v13a2 2 0 0 0 2 2h9a2 2 0 0 0 2-2V8l-4.5-4.5Z" />
      <path d="M14 3.5V8h4.5" />
      <path d="M9 13h6" />
      <path d="M9 16.5h4" />
    </Base>
  );
}

export function IconeSetaDireita(props: PropsIcone): JSX.Element {
  return (
    <Base {...props}>
      <path d="M5 12h14" />
      <path d="m13 6 6 6-6 6" />
    </Base>
  );
}

export function IconeFechar(props: PropsIcone): JSX.Element {
  return (
    <Base {...props}>
      <path d="m6 6 12 12" />
      <path d="m18 6-12 12" />
    </Base>
  );
}

/** Setas de ordenação da tabela. `direcao` indica qual ponta fica acesa. */
/** Clipe — há arquivo anexado. */
export function IconeClipe(props: PropsIcone): JSX.Element {
  return (
    <Base {...props}>
      <path d="m21 11.5-8.5 8.5a5.5 5.5 0 0 1-7.8-7.8l9-9a3.5 3.5 0 0 1 5 5l-9 9a1.5 1.5 0 0 1-2.1-2.1l8.3-8.3" />
    </Base>
  );
}

/** Lixeira — descarta um item só, não a lista. */
export function IconeLixeira(props: PropsIcone): JSX.Element {
  return (
    <Base {...props}>
      <path d="M4 7h16" />
      <path d="M10 11v6" />
      <path d="M14 11v6" />
      <path d="M5 7l1 12a2 2 0 0 0 2 2h8a2 2 0 0 0 2-2l1-12" />
      <path d="M9 7V5a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" />
    </Base>
  );
}

/** Lápis — corrige um valor já gravado, ali mesmo. */
export function IconeLapis(props: PropsIcone): JSX.Element {
  return (
    <Base {...props}>
      <path d="M16.5 3.5a2.1 2.1 0 0 1 3 3L8 18l-4 1 1-4Z" />
      <path d="m14.5 5.5 3 3" />
    </Base>
  );
}

/** Seta em cantoneira — o painel do mês abre e fecha. */
export function IconeCantoneira(props: PropsIcone): JSX.Element {
  return (
    <Base {...props}>
      <path d="m6 9 6 6 6-6" />
    </Base>
  );
}

/** Três pontos — o menu "mais ações" de uma linha. */
export function IconeMaisAcoes(props: PropsIcone): JSX.Element {
  return (
    <Base {...props} fill="currentColor" stroke="none">
      <circle cx="5" cy="12" r="1.75" />
      <circle cx="12" cy="12" r="1.75" />
      <circle cx="19" cy="12" r="1.75" />
    </Base>
  );
}

export function IconeOrdenacao({
  direcao,
  ...props
}: PropsIcone & { direcao: 'crescente' | 'decrescente' | 'nenhuma' }): JSX.Element {
  return (
    <Base {...props} className={juntarClasses('h-3.5 w-3.5', props.className)}>
      <path d="m8 10 4-4 4 4" opacity={direcao === 'decrescente' ? 0.3 : 1} />
      <path d="m8 14 4 4 4-4" opacity={direcao === 'crescente' ? 0.3 : 1} />
    </Base>
  );
}

/**
 * Espiral de carregamento.
 *
 * `data-movimento="essencial"` marca a exceção pedida na INTEGRACAO.md: a regra
 * global de `prefers-reduced-motion` zera a duração de toda animação, o que
 * congela este arco e faz o carregamento parecer travado. Enquanto a exceção não
 * existe em `tokens.css`, todo consumidor acompanha a espiral de um texto de
 * estado — o significado nunca depende do giro.
 */
export function IconeEspiral({ className, ...resto }: PropsIcone): JSX.Element {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      aria-hidden="true"
      focusable="false"
      data-movimento="essencial"
      className={juntarClasses('h-4 w-4 shrink-0 animate-spin', className)}
      {...resto}
    >
      <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth={2.5} opacity={0.25} />
      <path
        d="M21 12a9 9 0 0 0-9-9"
        stroke="currentColor"
        strokeWidth={2.5}
        strokeLinecap="round"
      />
    </svg>
  );
}
